import React, { useMemo, useState } from 'react';
import type { ArWorkbookParsed } from '../../types';
import {
  trackerMatch, assetMod, rowMod, regionOf, REGION_COLOR, cleanSponsor, firstAgent, phaseBucket, PHASE_ORDER,
} from './arHelpers';
import type { ArSharedProps, ArTabKey } from './AssetRadarPage';

/** Where a program can be opened from: its landscape column in a setting (asset name is that setting's
 * exact column header, for LandscapeTab's flash-and-scroll match), or a calendar-only trial mention. */
interface SettingRef { key: string; label: string }
interface LandscapeRef extends SettingRef { assetName: string }

interface Program {
  key: string;
  name: string; target: string; moa: string; modality: string; sponsor: string; region: string;
  settings: Set<string>; trials: number; hasData: boolean; phases: Set<string>; phase: string;
  landscapeSettings: Map<string, LandscapeRef>;
  calendarSettings: Map<string, SettingRef>;
}

function buildPrograms(wb: ArWorkbookParsed, emergingOnly: boolean, tracker: ReturnType<typeof import('./arHelpers').buildTracker> | null): Program[] {
  const map = new Map<string, Program>();
  const get = (key: string, init: Omit<Program, 'settings' | 'trials' | 'hasData' | 'phases' | 'phase' | 'key' | 'region' | 'landscapeSettings' | 'calendarSettings'>) => {
    if (!map.has(key)) {
      map.set(key, {
        ...init, key, region: '', settings: new Set(), trials: 0, hasData: false, phases: new Set(), phase: '',
        landscapeSettings: new Map(), calendarSettings: new Map(),
      });
    }
    return map.get(key)!;
  };
  for (const ind of wb.inds) {
    if (ind.land) for (const a of ind.land.assets) {
      if (emergingOnly && a.tier !== 3) continue;
      const m = trackerMatch(tracker, a.name);
      const fa = firstAgent(a.name);
      const key = m ? 't:' + m.asset.toLowerCase() : 'n:' + fa.toLowerCase();
      const p = get(key, {
        name: m ? m.asset : fa, target: m ? m.target : '', moa: m ? m.moa : '',
        modality: assetMod(tracker, a), sponsor: cleanSponsor(m?.sponsor || a.sponsor),
      });
      if (!p.moa && a.moa) p.moa = a.moa;
      p.settings.add(ind.label); p.hasData = true; p.phases.add(phaseBucket(a.phase));
      if (!p.landscapeSettings.has(ind.key)) p.landscapeSettings.set(ind.key, { key: ind.key, label: ind.label, assetName: a.name });
    }
    if (Array.isArray(ind.cal)) for (const r of ind.cal) {
      if (emergingOnly && r.tier !== 3) continue;
      const m = trackerMatch(tracker, `${r.regimen} ${r.trial}`);
      const fa = firstAgent(r.regimen || r.trial);
      if (!m && !fa) continue;
      const key = m ? 't:' + m.asset.toLowerCase() : 'n:' + fa.toLowerCase();
      const p = get(key, {
        name: m ? m.asset : fa, target: m ? m.target : '', moa: m ? m.moa : '',
        modality: rowMod(tracker, r), sponsor: cleanSponsor(m?.sponsor || r.sponsor),
      });
      p.settings.add(ind.label); p.trials++; p.phases.add(phaseBucket(r.phaseN || r.phase));
      if (!p.calendarSettings.has(ind.key)) p.calendarSettings.set(ind.key, { key: ind.key, label: ind.label });
    }
  }
  const out = [...map.values()];
  for (const p of out) {
    p.phase = PHASE_ORDER.find((x) => p.phases.has(x)) || 'Not stated';
    p.region = regionOf(p.sponsor);
    if (!p.target) p.target = 'Not in tracker';
    if (!p.moa) p.moa = 'MOA not stated';
  }
  return out;
}

interface JumpTarget { setting: SettingRef; tab: 'landscape' | 'timeline'; assetName?: string }

/** Every place this program can be opened: its landscape column in each setting that has one, plus a
 * Timeline search for any OTHER setting where it's only a calendar mention (a setting with both just
 * opens the landscape, same as clicking an asset that has real data). */
function jumpTargetsOf(p: Program): JumpTarget[] {
  const out: JumpTarget[] = [...p.landscapeSettings.values()].map((s) => ({ setting: s, tab: 'landscape', assetName: s.assetName }));
  for (const s of p.calendarSettings.values()) if (!p.landscapeSettings.has(s.key)) out.push({ setting: s, tab: 'timeline' });
  return out;
}

interface TallyItem { key: string; label: string; count: number; progs: Program[]; other?: boolean }
function tally(progs: Program[], keyFn: (p: Program) => string | string[], top: number, order?: string[]): TallyItem[] {
  const m = new Map<string, Program[]>();
  for (const p of progs) {
    const ks = ([] as string[]).concat(keyFn(p) as any);
    for (const k of ks) { if (!m.has(k)) m.set(k, []); m.get(k)!.push(p); }
  }
  let items: TallyItem[] = [...m.entries()].map(([k, list]) => ({ key: k, label: k, count: list.length, progs: list }));
  if (order) items.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  else items.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  if (top && items.length > top) {
    const rest = items.slice(top);
    items = items.slice(0, top);
    items.push({ key: '__other', label: `Other (${rest.length})`, count: rest.reduce((n, x) => n + x.count, 0), progs: rest.flatMap((x) => x.progs), other: true });
  }
  return items;
}

const PALETTE = ['var(--c0)', 'var(--c1)', 'var(--c2)', 'var(--c3)', 'var(--c4)', 'var(--c5)', 'var(--c6)', 'var(--c7)'];
const pal = (i: number) => PALETTE[i % PALETTE.length];
const colorIdx = (it: TallyItem, i: number) => (it.other ? 'var(--c-other)' : pal(i));
const TIERCOL: Record<string, string> = { Approved: 'var(--t1)', 'Phase 3': 'var(--t3)', 'Phase 2': 'var(--c0)', 'Phase 1/2': 'var(--c3)', 'Phase 1': 'var(--c1)', 'Not stated': 'var(--c-other)' };

const ProgramChips: React.FC<{ progs: Program[]; onJump: (p: Program) => void }> = ({ progs, onJump }) => (
  <div className="progs">
    {progs.slice().sort((a, b) => PHASE_ORDER.indexOf(a.phase) - PHASE_ORDER.indexOf(b.phase) || a.name.localeCompare(b.name)).map((p) => (
      <button
        key={p.key}
        type="button"
        className="prog"
        title={`${p.sponsor}; ${[...p.settings].join(', ')}${p.hasData ? '' : '; no clinical data yet (calendar only)'}`}
        onClick={() => onJump(p)}
      >
        {p.name}
        <small>{p.phase.replace('Phase ', 'Ph ')}{p.hasData ? '' : ', no data'}</small>
      </button>
    ))}
  </div>
);

const CompBlock: React.FC<{
  id: string; title: string; sub?: string | null; items: TallyItem[]; total: number; unit: string;
  colorFn: (it: TallyItem, i: number) => string; wide?: boolean; onJump: (p: Program) => void; legend?: React.ReactNode;
}> = ({ id, title, sub, items, total, unit, colorFn, wide, onJump, legend }) => {
  const [sel, setSel] = useState<string | null>(null);
  const sum = items.reduce((n, x) => n + x.count, 0) || 1;
  const max = Math.max(...items.map((x) => x.count), 1);
  return (
    <section data-block-id={id} className={`ov-block${wide ? ' wide' : ''}`} aria-label={title}>
      <div className="ov-h">
        <span className="cap">{title}</span>
        <span className="ov-n">{total} {unit}</span>
      </div>
      {sub && <p className="ov-sub">{sub}</p>}
      {legend}
      <div className="stack" aria-hidden="true">
        {items.map((it, i) => {
          const pc = (it.count / sum) * 100;
          return (
            <i key={it.key} style={{ width: `${pc}%`, background: colorFn(it, i) }}>
              {pc >= 9 && <span>{Math.round(pc)}%</span>}
            </i>
          );
        })}
      </div>
      <div className="olist">
        {items.map((it, i) => {
          const open = sel === it.key;
          return (
            <React.Fragment key={it.key}>
              <button type="button" className="orow" aria-expanded={open} title={it.label} onClick={() => setSel(open ? null : it.key)}>
                <span className="sw" style={{ background: colorFn(it, i) }} />
                <span className="lb">{it.label}</span>
                <span className="bar"><i style={{ width: `${(it.count / max) * 100}%`, background: colorFn(it, i) }} /></span>
                <span className="ct">{it.count}</span>
                <span className="pc">{Math.round((it.count / total) * 100)}%</span>
              </button>
              {open && <ProgramChips progs={it.progs} onJump={onJump} />}
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
};

const HeatBlock: React.FC<{ progs: Program[]; onJump: (p: Program) => void }> = ({ progs, onJump }) => {
  const [sel, setSel] = useState<{ t: string; m: string } | null>(null);
  const targets = useMemo(() => {
    const t = tally(progs.filter((p) => p.target !== 'Not in tracker'), (p) => p.target, 0).slice(0, 14).map((x) => x.key);
    if (progs.some((p) => p.target === 'Not in tracker')) t.push('Not in tracker');
    return t;
  }, [progs]);
  const mods = useMemo(() => tally(progs, (p) => p.modality, 0).map((x) => x.key), [progs]);
  const cell = (t: string, m: string) => progs.filter((p) => p.target === t && p.modality === m);
  const max = Math.max(1, ...targets.flatMap((t) => mods.map((m) => cell(t, m).length)));
  if (!targets.length) return null;
  return (
    <section className="ov-block wide" aria-label="Target by modality">
      <div className="ov-h">
        <span className="cap">Target &times; modality</span>
        <span className="ov-n">Where programs cluster</span>
      </div>
      <p className="ov-sub">Counts of programs for the busiest targets. Select a cell to list them.</p>
      <div className="hm">
        <table className="hmt">
          <thead>
            <tr>
              <th className="tl" scope="col">Target</th>
              {mods.map((m) => <th key={m} scope="col">{m}</th>)}
            </tr>
          </thead>
          <tbody>
            {targets.map((tg) => (
              <tr key={tg}>
                <td className="tl">{tg}</td>
                {mods.map((m) => {
                  const list = cell(tg, m), k = list.length;
                  const on = sel && sel.t === tg && sel.m === m;
                  const pc = 12 + (k / max) * 70;
                  const pick = () => setSel(on ? null : { t: tg, m });
                  return (
                    <td
                      key={m}
                      className={`hc${k ? '' : ' z'}${on ? ' on' : ''}`}
                      tabIndex={k ? 0 : undefined}
                      aria-label={`${tg}, ${m}: ${k} programs`}
                      style={k ? { background: `color-mix(in srgb, var(--accent) ${pc}%, var(--surface))`, color: pc > 50 ? 'var(--on-accent)' : 'var(--ink)' } : undefined}
                      onClick={k ? pick : undefined}
                      onKeyDown={k ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } } : undefined}
                    >
                      {k || '·'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sel && cell(sel.t, sel.m).length > 0 && (
        <div className="hm-sel">
          <strong>{sel.t} &times; {sel.m}</strong>
          <ProgramChips progs={cell(sel.t, sel.m)} onJump={onJump} />
        </div>
      )}
    </section>
  );
};

interface OverviewTabProps extends ArSharedProps {
  onJump: (opts: { tab: ArTabKey; setting?: string; q?: string; asset?: string }) => void;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ workbook, tracker, trackerError, onJump }) => {
  const [emergingOnly, setEmergingOnly] = useState(true);
  const [picker, setPicker] = useState<{ name: string; targets: JumpTarget[] } | null>(null);
  const progs = useMemo(() => (workbook ? buildPrograms(workbook, emergingOnly, tracker) : []), [workbook, emergingOnly, tracker]);

  const goTo = (t: JumpTarget, name: string) => {
    setPicker(null);
    if (t.tab === 'landscape') onJump({ tab: 'landscape', setting: t.setting.key, asset: t.assetName || name });
    else onJump({ tab: 'timeline', setting: t.setting.key, q: name });
  };

  // A program with data in only one setting opens straight there; more than one setting (it may have a
  // landscape column in some and only a calendar mention in others) asks which one via a popup.
  const jumpProgram = (p: Program) => {
    const targets = jumpTargetsOf(p);
    if (!targets.length) return;
    if (targets.length === 1) { goTo(targets[0], p.name); return; }
    setPicker({ name: p.name, targets });
  };

  if (!workbook) return null;
  const n = progs.length;

  return (
    <>
      {picker && (
        <div className="ov-picker-backdrop" role="presentation" onClick={() => setPicker(null)}>
          <div className="ov-picker" role="dialog" aria-modal="true" aria-label={`Choose a setting for ${picker.name}`} onClick={(e) => e.stopPropagation()}>
            <h3>{picker.name}</h3>
            <p className="note">Which setting do you want to open?</p>
            <div className="ov-picker-list">
              {picker.targets.map((t) => (
                <button key={t.setting.key} type="button" className="ov-picker-opt" onClick={() => goTo(t, picker.name)}>
                  <span>{t.setting.label}</span>
                  <small>{t.tab === 'landscape' ? 'Landscape' : 'Timeline (calendar only)'}</small>
                </button>
              ))}
            </div>
            <button type="button" className="btn ghost" onClick={() => setPicker(null)}>Cancel</button>
          </div>
        </div>
      )}
      <div className="ov-scope">
        <div className="seg" role="group" aria-label="Scope">
          {[[true, 'Emerging only'], [false, 'All, incl. SOC']].map(([v, l]) => (
            <button key={String(v)} type="button" aria-pressed={emergingOnly === v} onClick={() => setEmergingOnly(v as boolean)}>{l as string}</button>
          ))}
        </div>
        <span className="note">
          {tracker ? 'Targets, MOAs and sponsors come from your Target-MOA Tracker, matched to each landscape and calendar entry. Select any row to see its programs.'
            : trackerError ? 'The Target-MOA Tracker couldn’t be read, so targets and MOAs show as not in tracker.'
            : 'Loading the Target-MOA Tracker for targets and MOAs…'}
        </span>
      </div>

      {!progs.length ? (
        <div className="state"><h2>No programs to summarize</h2><p>This workbook has no landscape or calendar entries in scope.</p></div>
      ) : (
        <div className="ov-grid">
          <CompBlock id="mod" title="Modality" sub="Drug type, from the tracker’s modality class." items={tally(progs, (p) => p.modality, 7)} total={n} unit="programs" colorFn={colorIdx} onJump={jumpProgram} />
          <CompBlock id="phase" title="Most advanced phase" sub="Highest phase reached in this disease, across all settings." items={tally(progs, (p) => p.phase, 0, PHASE_ORDER)} total={n} unit="programs" colorFn={(it) => TIERCOL[it.key] || 'var(--c-other)'} onJump={jumpProgram} />
          <CompBlock id="target" title="Target" sub="Molecular target of each program." items={tally(progs, (p) => p.target, 10)} total={n} unit="programs" colorFn={colorIdx} onJump={jumpProgram} />
          <CompBlock id="moa" title="Mechanism of action" items={tally(progs, (p) => p.moa, 10)} total={n} unit="programs" colorFn={colorIdx} onJump={jumpProgram} />
          <CompBlock
            id="sponsor" title="Sponsors" items={tally(progs, (p) => p.sponsor, 12)} total={n} unit="programs"
            colorFn={(it) => (it.other ? 'var(--c-other)' : REGION_COLOR[regionOf(it.key)])} onJump={jumpProgram}
            legend={
              <div className="rlegend">
                {tally(progs, (p) => p.region, 0, ['Global', 'China', 'Japan', 'Korea']).map((r) => (
                  <span key={r.key}><i style={{ background: REGION_COLOR[r.key] }} />{r.key} {r.count} ({Math.round((r.count / n) * 100)}%)</span>
                ))}
              </div>
            }
          />
          <CompBlock
            id="setting" title="Activity by setting" sub="Programs per setting; a program active in several settings counts in each."
            items={tally(progs, (p) => [...p.settings], 0, workbook.inds.map((i) => i.label))} total={n} unit="programs"
            colorFn={(_it, i) => pal(i + 3)} onJump={jumpProgram}
          />
          <HeatBlock progs={progs} onJump={jumpProgram} />
        </div>
      )}
    </>
  );
};

export default OverviewTab;
